import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaExternalLinkAlt, FaUsers, FaCheckCircle, FaRupeeSign } from 'react-icons/fa';
import { AffiliateApiService } from '../../service/affiliateApiService';
import { useLoader } from '../../base/context/LoaderProvider';
import './affiliate.css';

export default function AffiliateMySellers() {
  const [sellers, setSellers] = useState([]);
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programSettings, setProgramSettings] = useState(null);
  const loader = useLoader();
  const navigate = useNavigate();

  useEffect(() => {
    AffiliateApiService.getSettings()
      .then((res) => {
        if (res.status?.code === 200 && res.data) setProgramSettings(res.data);
      })
      .catch(() => {});
  }, []);

  const fetchSellers = () => {
    setLoading(true);
    AffiliateApiService.getSellers({ eligible: eligibleOnly ? 'true' : '' })
      .then((res) => {
        setLoading(false);
        if (res.status?.code === 200 && res.data?.sellers) {
          setSellers(res.data.sellers);
        } else {
          setError(res.error?.message || 'Failed to load');
        }
      })
      .catch((err) => {
        setLoading(false);
        if (err?.status === 403) {
          navigate('/', { replace: true });
          return;
        }
        setError(err?.message || 'Failed to load');
      });
  };

  useEffect(() => {
    fetchSellers();
  }, [eligibleOnly]);

  const filteredSellers = useMemo(() => {
    if (!search.trim()) return sellers;
    const q = search.trim().toLowerCase();
    return sellers.filter(
      (s) =>
        (s.firstName || '').toLowerCase().includes(q) ||
        (s.lastName || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
    );
  }, [sellers, search]);

  const openOrderDetailsInNewTab = (seller) => {
    window.open(`/seller/affiliate/sellers/${seller._id}/orders`, '_blank', 'noopener,noreferrer');
  };

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '–');
  const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  const totalGMV = sellers.reduce((sum, s) => sum + (s.totalSales || 0), 0);
  const eligibleCount = sellers.filter((s) => s.eligible).length;
  const eligibleThreshold = programSettings?.eligibleThreshold ?? 20000;
  const bonusDays = programSettings?.bonusDaysAfterDelivery ?? 15;

  if (error && sellers.length === 0) {
    return (
      <div className="affiliate-page">
        <h1 className="affiliate-welcome">My Sellers</h1>
        <p className="affiliate-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="affiliate-page">
      <h1 className="affiliate-welcome">My Sellers</h1>
      <p className="affiliate-subtitle">Sellers referred by your link. Only delivered order value counts for eligibility; bonus is credited 15 days after they cross ₹20K delivered. Click a row to open their orders.</p>

      {!loading && sellers.length > 0 && (
        <div className="affiliate-stats-strip">
          <span className="affiliate-stats-strip-item"><FaUsers style={{ marginRight: '0.35rem', verticalAlign: 'middle', opacity: 0.8 }} /><strong>{sellers.length}</strong> total sellers</span>
          <span className="affiliate-stats-strip-item"><FaCheckCircle style={{ marginRight: '0.35rem', verticalAlign: 'middle', opacity: 0.8 }} /><strong>{eligibleCount}</strong> eligible</span>
          <span className="affiliate-stats-strip-item"><FaRupeeSign style={{ marginRight: '0.35rem', verticalAlign: 'middle', opacity: 0.8 }} /><strong>{formatMoney(totalGMV)}</strong> total GMV</span>
        </div>
      )}

      <div className="affiliate-toolbar">
        <div className="affiliate-form-group">
          <label htmlFor="affiliate-seller-search">Search by name or email</label>
          <input
            id="affiliate-seller-search"
            type="search"
            placeholder="Search sellers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#475569', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={eligibleOnly}
            onChange={(e) => setEligibleOnly(e.target.checked)}
          />
          Only eligible (₹{Number(eligibleThreshold).toLocaleString('en-IN')} delivered + {bonusDays} days passed)
        </label>
      </div>

      {loading ? (
        <p className="affiliate-loading">Loading sellers…</p>
      ) : (
        <div className="affiliate-table-wrap">
          <table className="affiliate-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Join date</th>
                <th>Delivered sales</th>
                <th>Eligible</th>
                <th style={{ width: '80px' }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredSellers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="affiliate-orders-empty">
                    {sellers.length === 0 ? 'No sellers yet. Share your referral link to get started.' : 'No sellers match your search.'}
                  </td>
                </tr>
              ) : (
                filteredSellers.map((s) => (
                  <tr
                    key={s._id}
                    className="affiliate-seller-row-clickable"
                    onClick={() => openOrderDetailsInNewTab(s)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOrderDetailsInNewTab(s); } }}
                  >
                    <td>{[s.firstName, s.lastName].filter(Boolean).join(' ') || '–'}</td>
                    <td>{s.email || '–'}</td>
                    <td>{formatDate(s.joinDate)}</td>
                    <td>{formatMoney(s.totalSales)}</td>
                    <td>
                      <span className={`affiliate-badge ${s.eligible ? 'affiliate-badge-eligible' : 'affiliate-badge-pending'}`}>
                        {s.eligible ? 'Eligible' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="affiliate-btn affiliate-btn-ghost"
                        onClick={(e) => { e.stopPropagation(); openOrderDetailsInNewTab(s); }}
                        title="View orders"
                      >
                        <FaExternalLinkAlt size={12} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
